import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function getNavbarBottom() {
  const navbar = document.querySelector('.navbar-header');
  return navbar ? Math.ceil(navbar.getBoundingClientRect().bottom) : 72;
}

function captureHeaderLayout(table, previousLayout = null) {
  if (!table) {
    return null;
  }

  const thead = table.querySelector('thead');
  if (!thead) {
    return null;
  }

  const trs = thead.querySelectorAll('tr');
  if (trs.length === 0) {
    return null;
  }

  const rows = Array.from(trs).map((tr, rowIndex) =>
    Array.from(tr.querySelectorAll('th')).map((th, colIndex) => ({
      className: th.className,
      text: th.textContent ?? '',
      colspan: th.colSpan > 1 ? th.colSpan : undefined,
      rowspan: th.rowSpan > 1 ? th.rowSpan : undefined,
      sortable: th.classList.contains('sortable-th'),
      width: th.getBoundingClientRect().width
        || previousLayout?.rows?.[rowIndex]?.[colIndex]?.width
        || 0,
    })),
  );

  return {
    tableClassName: table.className,
    tableWidth: table.offsetWidth || previousLayout?.tableWidth || 0,
    height: thead.getBoundingClientRect().height || previousLayout?.height || 0,
    rows,
  };
}

/**
 * En-tête de tableau fixé sous la navbar au scroll de page (sans scroll interne vertical).
 */
const StickyTableWrap = ({ children, className = '', style, ...rest }) => {
  const scrollRef = useRef(null);
  const tableRef = useRef(null);
  const stuckRef = useRef(false);
  const headerLayoutRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  const [headerLayout, setHeaderLayout] = useState(null);
  const [metrics, setMetrics] = useState({
    top: 72,
    left: 0,
    width: 0,
    height: 0,
    scrollLeft: 0,
  });

  const refreshHeaderLayout = useCallback(() => {
    const layout = captureHeaderLayout(tableRef.current, headerLayoutRef.current);
    if (layout) {
      headerLayoutRef.current = layout;
      setHeaderLayout(layout);
    }
    return layout;
  }, []);

  const update = useCallback(() => {
    const table = tableRef.current;
    const scroll = scrollRef.current;
    if (!table || !scroll) {
      return;
    }

    const thead = table.querySelector('thead');
    if (!thead) {
      return;
    }

    const top = getNavbarBottom();
    const theadRect = thead.getBoundingClientRect();
    const shouldStick = theadRect.top < top - 1;

    if (shouldStick && !stuckRef.current) {
      refreshHeaderLayout();
    }

    stuckRef.current = shouldStick;
    setStuck(shouldStick);
    setMetrics({
      top,
      left: scroll.getBoundingClientRect().left,
      width: scroll.clientWidth,
      height: headerLayoutRef.current?.height ?? theadRect.height,
      scrollLeft: scroll.scrollLeft,
    });
  }, [refreshHeaderLayout]);

  useLayoutEffect(() => {
    refreshHeaderLayout();
  }, [children, refreshHeaderLayout]);

  useEffect(() => {
    update();

    const onScroll = () => {
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true, capture: true });

    const onResize = () => {
      refreshHeaderLayout();
      update();
    };
    window.addEventListener('resize', onResize);

    const scroll = scrollRef.current;
    let resizeObserver;
    if (scroll && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        refreshHeaderLayout();
        update();
      });
      resizeObserver.observe(scroll);
      if (tableRef.current) {
        resizeObserver.observe(tableRef.current);
      }
    }

    scroll?.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onResize);
      scroll?.removeEventListener('scroll', onScroll);
      resizeObserver?.disconnect();
    };
  }, [update, refreshHeaderLayout]);

  const setTableRef = useCallback((node) => {
    tableRef.current = node;
    if (node) {
      refreshHeaderLayout();
      update();
    }
  }, [refreshHeaderLayout, update]);

  const child = React.Children.only(children);

  const forwardSortClick = useCallback((rowIndex, colIndex) => {
    const table = tableRef.current;
    if (!table) return;
    const tr = table.querySelectorAll('thead tr')[rowIndex];
    const th = tr?.querySelectorAll('th')[colIndex];
    if (th && th.classList.contains('sortable-th')) {
      th.click();
      // Refresh sticky labels/icons after sort state updates.
      window.requestAnimationFrame(() => {
        refreshHeaderLayout();
        update();
      });
    }
  }, [refreshHeaderLayout, update]);

  const stickyHead = stuck && headerLayout
    ? createPortal(
      <div
        className="app-table-sticky-head is-visible"
        style={{
          top: metrics.top,
          left: metrics.left,
          width: metrics.width,
          height: metrics.height || undefined,
        }}
        aria-hidden={false}
      >
        <div
          className="app-table-sticky-head__inner"
          style={{ transform: `translateX(-${metrics.scrollLeft}px)` }}
        >
          <table
            className={headerLayout.tableClassName}
            style={{ width: headerLayout.tableWidth }}
          >
            <thead>
              {headerLayout.rows.map((row, rowIndex) => (
                <tr key={`sticky-row-${rowIndex}`}>
                  {row.map((cell, colIndex) => (
                    <th
                      key={`sticky-cell-${rowIndex}-${colIndex}-${cell.text}`}
                      className={cell.className}
                      colSpan={cell.colspan}
                      rowSpan={cell.rowspan}
                      style={{
                        width: cell.width,
                        minWidth: cell.width,
                        maxWidth: cell.width,
                      }}
                      onClick={cell.sortable ? () => forwardSortClick(rowIndex, colIndex) : undefined}
                      onKeyDown={cell.sortable ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          forwardSortClick(rowIndex, colIndex);
                        }
                      } : undefined}
                      role={cell.sortable ? 'columnheader' : undefined}
                      tabIndex={cell.sortable ? 0 : undefined}
                    >
                      {cell.text}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
          </table>
        </div>
      </div>,
      document.body,
    )
    : null;

  const containerClassName = [
    'app-table-sticky-container',
    'table-responsive',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={`app-table-sticky-wrap${stuck ? ' is-stuck' : ''}`}>
      {stickyHead}
      <div className={containerClassName} style={style} ref={scrollRef} {...rest}>
        {React.cloneElement(child, { ref: setTableRef })}
      </div>
    </div>
  );
};

export default StickyTableWrap;
